// Ask AI's system prompt and tools for the Worklist and History: plain functions of what the page
// shows, used by `axai.ts` (the React side). The definitions are `ai-guidance.ts`'s, shared with
// the lines reports.

import {
  APP_PAGES,
  HISTORY_GUIDANCE,
  PAGES,
  REVIEW_LABELS,
  ROUTING_API,
  ROUTING_DEFINITIONS,
  STATUS_CATEGORIES,
  STATE_TAB_LABELS,
  VIEW_PARTS,
  WAREHOUSE_GROUPS,
  WINDOW_STATE_LABELS,
  WORKLIST_GUIDANCE,
  toSearch,
  weekRange,
} from "@app/shared";
import type { GridView, LineSource, Mode, Scope } from "@app/shared";

export type SystemBlock = { type: "text"; text: string };
export interface Tool {
  name: string;
  description: string;
  input_schema: Record<string, unknown>;
}
export interface AxaiApp {
  systemPrompt(): SystemBlock[];
  handleToolCall(
    name: string,
    input: unknown,
    appendStatus: (text: string) => void,
    isStale: () => boolean,
  ): Promise<string>;
}
/** What the page tells Ask AI, read fresh at each question. */
export interface AskAiPage {
  mode: Mode;
  today: string;
  view: GridView;
  apply: (next: GridView, unapplied: string[]) => void;
  /** The line ids a count's scope keeps (`scopeOf`), counted as the grid counts them. */
  lineIdsFor: (scope: Scope) => string[];
  /** Every line id of this page, whatever the filters — within My customers while it is on. */
  allLineIds: () => string[];
  /** Every line id of this page, My customers too: what a read's `within` / `outside` is relative to. */
  everyLineId: () => string[];
  /** A grid column's header, for the scope in words. */
  headerOf: (column: string) => string;
  /** The lines a view would show, and per tab, counted as the grid counts them. */
  shownFor: (view: GridView) => { shown: number; counts: Record<string, number> };
  /** Lines per tab on this page, as the tabs show them. */
  counts: Record<string, number>;
  /** Lines on screen with the current filters, and pick tickets waiting for the WMS. */
  shown: number;
  /** The customer names on this page, so `apply_view` never sets one that shows nothing. */
  customers: readonly string[];
  /** Their codes (what the Customer column shows for A2000) → the name: `apply_view` takes either. */
  customerCodes: Readonly<Record<string, string>>;
  /** The divisions' abbreviations (what the Division column shows) → their full names. */
  divisionNames: Readonly<Record<string, readonly string[]>>;
  /** Open pick tickets with no WMS # yet: the No WMS tab. */
  noWms: number;
  /** The signed-in person's customers (`assignments.ts`): "my customers"; `on`: the grid shows only them. */
  myCustomers: { on: boolean; labels: readonly string[]; codes: readonly string[] };
  /** Lines per source with the other filters (the Source filter's counts). */
  sourceCounts: Record<LineSource, number>;
  /** Deposco orders with a DC and no group: not lines yet. */
  deposcoWaiting: number;
  manager: boolean;
}

const list = (values: readonly string[]) => values.map((v) => `"${v}"`).join(", ");

const COLUMN_HELP = {
  worklist:
    `Choice columns and their values: source ("A2000", "Deposco": the Source filter); review ` +
    `(${list(Object.values(REVIEW_LABELS))}); state_tab, the Worklist tab ` +
    `(${list(STATE_TAB_LABELS)}); window_state (${list(Object.values(WINDOW_STATE_LABELS))}); routing_status ` +
    `(a value of the managed Routing status list); routing_category (${list(STATUS_CATEGORIES)}: what the status ` +
    `means — Routed counts as routed); warehouse (${list(WAREHOUSE_GROUPS)}); mixed ("Yes", "No"); two_values ("Yes", ` +
    `"No": a "2 values" choice open); rule_source (text: where the routing window came from and its days, in Admin → Routing rules' words, e.g. "From A2000 · code BEALLS in this ` +
    `group · 10 / 3"; the source is one of Set in the app, Set in the app for <code>, From A2000 (this customer), From A2000 · code <code> in this group, From A2000 · same name as <code>, Its own window: kept separate from <name>, Turned off in the app; No window — Admin → Routing rules, else A2000's CUST_REF1/2); shipped_in_a2000 ` +
    `("All", "Some", "No": pick tickets SHIPPED or BILLED in the WMS, a flag only); shipped_in_deposco ("All", ` +
    `"Some", "No": a Deposco line's SOs Ship Complete or with a ship date, a flag only); deposco_status (a Deposco ` +
    `line's SOs' highest status, "· MIXED" when they differ); deposco_mixed ("Yes", "No"); customer (any of ` +
    `the line's customers as the grid shows them — a shown name set in Admin → Customers, else A2000's code or Deposco's name; a shown name keeps every code showing it; the original name or the code finds that code), customer_name (the original name), customer_code (A2000 CUSTOMER or a Deposco trading partner's code), division (the full ` +
    `name), division_abbrev (what the grid shows), dc, inventory_notes, lead, in_wms ("Yes", "No": No is a No WMS ` +
    `row), changed_outside ("Yes", "No"). Dates (YYYY-MM-DD): received, first_ship, cancel, ready_date, ` +
    `sched_ship, appointment_date (the date of the pickup the line is on; empty on none), route_by, window_opens, routed_on. Numbers: days_left (business days to Route ` +
    `by, negative = late), qty, pick_ticket_count. Text: line_id, wms (the WMS #), so_pt (SO / PT #), po (PO), ` +
    `pick_tickets, routing_ref, routing_window, work_needed (one or several values, comma-separated: a filter matches ` +
    `a line holding any), production_notes, routing_notes, changes, came_from, conflicts, ` +
    `dlix, co, group (Deposco's numbers), deposco_sos (each SO and its status). The PE ORDERS columns ` +
    `(hidden on the grid until a person shows them): pe_status (PENDING, PRINTED, IN PROCESS, PAUSED, COMPLETE, ` +
    `CANCELLED; prefilled from Deposco's status or Shipped in A2000, pe_status_prefilled "Yes"), pe_ship_date ` +
    `(date; READY DATE − 2 business days until one is picked, pe_ship_date_prefilled "Yes"; pe_ship_date_text ` +
    `ASAP), pe_pull_group, pe_loc_qty, pe_assigned_to (one or several people), pe_estimate_minutes, pe_start, ` +
    `pe_end (YYYY-MM-DD HH:MM), pe_pallets (the Shipping schedule's PLTS too), pe_comments.`,
  history:
    `Choice columns and their values: source ("A2000", "Deposco": the Source filter; a workbook row is A2000); ` +
    `origin ("App", "Excel 2026"); kind; finished ("Shipped", "Cancelled"); ` +
    `finished_on_approx ("Yes", "No"); duplicate ("Yes", "No"); routing_status (the Routing status list's); ` +
    `warehouse (${list(WAREHOUSE_GROUPS)}); mixed ("Yes", "No"); customer, customer_code, division, ` +
    `division_abbrev, dc, work_needed, lead. ` +
    `finished is also "Regrouped by WMS" (a line from before WMS grouping). Dates (YYYY-MM-DD): finished_on, ` +
    `received, first_ship, cancel, ready_date, sched_ship, appointment_date, routed_on. Numbers: qty, ` +
    `pick_ticket_count. Text: line_id, wms, so_pt (SO / PT #), po (PO), pick_tickets, routing_ref, finished_note.`,
};

function queryProps(history: boolean): Record<string, unknown> {
  return {
    filters: {
      type: "object",
      description:
        "Column name → the values to keep (any of them; exact, case-insensitive). A misspelt or partial value " +
        "matches nothing and the answer lists close values under no_such_value: ask again with one of them.",
      additionalProperties: { type: "array", items: { type: "string" } },
    },
    dates: {
      type: "array",
      description: "Date ranges, YYYY-MM-DD, either end open.",
      items: {
        type: "object",
        properties: { column: { type: "string" }, from: { type: "string" }, to: { type: "string" } },
        required: ["column"],
      },
    },
    numbers: {
      type: "array",
      description: "Number ranges, either end open (e.g. qty, days_left, pick_ticket_count).",
      items: {
        type: "object",
        properties: { column: { type: "string" }, min: { type: "number" }, max: { type: "number" } },
        required: ["column"],
      },
    },
    search: {
      type: "string",
      description: "A pick ticket, SO #, PO, WMS shipment, routing reference, or a Deposco DLIX, CO, group or DC.",
    },
    ignore_view: {
      type: "array",
      items: { type: "string", enum: [...VIEW_PARTS, "all"] },
      description:
        "Parts of the user's current view NOT to count within, only when the question says so: 'in all " +
        "warehouses' → warehouse, 'any date' / 'ever' → date, 'all customers' → customer, 'everything' → all. " +
        "What the query filters on itself replaces that part of the view anyway.",
    },
    ...(history
      ? {
          all_history: {
            type: "boolean",
            description:
              "true: also non-orders, workbook duplicates and lines finished before 2026. Default false: " +
              "History as its page shows it.",
          },
        }
      : {}),
  };
}

const READ_PROPS = (history: boolean) => ({
  ...queryProps(history),
  group_by: { type: "string", description: "Count the matching lines per value of this column." },
  columns: { type: "array", items: { type: "string" }, description: "Columns to list (default: a short set)." },
  sort: { type: "string" },
  desc: { type: "boolean" },
  limit: { type: "integer", description: "Lines to list, 0–25 (default 10). Counts cover every match." },
});

export function toolsFor(mode: Mode, manager: boolean): Tool[] {
  const tools: Tool[] = [
    {
      name: "query_worklist",
      description:
        "Count, group and list the OPEN lines of the Worklist (routing-worklist-lines), within the user's " +
        "current view (warehouses, date range, search, customer, routing status, tab) unless ignore_view says " +
        `otherwise. ${COLUMN_HELP.worklist} Use it for every number you give about open lines. The answer ` +
        "says its scope, and matched_without_view: the same count without the view.",
      input_schema: { type: "object", properties: READ_PROPS(false) },
    },
    {
      name: "query_history",
      description:
        "Count, group and list the FINISHED lines of History (routing-history-lines): shipped or cancelled, " +
        `the app's own and the old workbook's. ${COLUMN_HELP.history} Within the user's current view on ` +
        "History (as query_worklist on the Worklist); from the Worklist, as the History page shows it by default.",
      input_schema: { type: "object", properties: READ_PROPS(true) },
    },
    {
      name: "explain_routing_window",
      description:
        "One open line's routing window: its rule (open_rule_days before FIRST SHIP, due_rule_days before " +
        "CANCEL, in business days), Window opens, Route by, days left, the label, and holidays_skipped: the " +
        "holidays the count stepped over. Name them when you explain the dates.",
      input_schema: {
        type: "object",
        properties: { pick_ticket: { type: "string" }, line_id: { type: "string" } },
      },
    },
    {
      name: "apply_view",
      description:
        `Set the grid on the page the user is on (now: ${mode === "worklist" ? "the Worklist" : "History"}) in one ` +
        "step, with Undo. Only what you give changes: the rest of the user's view (warehouses, date range, search, " +
        "…) stays. To remove a filter, name its part in clear ('in all warehouses' → warehouse). The same terms as " +
        `query_${mode}. A Worklist tab is filters.state_tab (one value), filters.review ["New", "Updated", "Split"] ` +
        `for To review, filters.state_tab ["No WMS"] for No WMS, filters.mixed ["Yes"] for Mixed, ` +
        `filters.two_values ["Yes"] for 2 values, ` +
        `filters.shipped_in_a2000 ["All", "Some"] for Shipped in A2000, filters.shipped_in_deposco ["All", "Some"] ` +
        `for Shipped in Deposco, or window_state; the Source filter is filters.source ["A2000"] or ["Deposco"] (both: ` +
        `clear source); ` +
        `"route today" is filters.state_tab ["Past deadline", "Route now"]; a History tab is filters.finished. ` +
        "One date range at a time, replacing the grid's. Use it when " +
        "the user asks to see or show lines. Give everything the user asked for, even what this grid cannot show (a " +
        "number range, a grouping): it comes back under not_applied, and you must tell the user. Customer names must be " +
        "exact: an unknown one is refused with close names.",
      input_schema: {
        type: "object",
        properties: {
          ...queryProps(mode === "history"),
          clear: {
            type: "array",
            items: { type: "string", enum: [...VIEW_PARTS, "all"] },
            description: "Parts of the current view to remove, only when the user asks (e.g. 'all warehouses').",
          },
          group_by: { type: "string", description: "The grid cannot group: given, it is named as not applied." },
          sort: { type: "string" },
          desc: { type: "boolean" },
        },
      },
    },
  ];
  tools.push({
    name: "deposco_waiting",
    description:
      "The Deposco orders waiting for a group: open (New ones too), with a DC and no group in Deposco, so not lines yet. How " +
      "many, by customer, and the first ones (SO #, customer, PO, DC, Deposco status, dates, qty). Optional " +
      "customer (names or parts of them) and search.",
    input_schema: {
      type: "object",
      properties: {
        customer: { type: "array", items: { type: "string" } },
        search: { type: "string" },
        limit: { type: "integer", description: "Orders to list, 0–25 (default 10)." },
      },
    },
  });
  if (manager)
    tools.push({
      name: "order_value",
      description:
        "Managers only: the order $ value of open Worklist lines (A2000 pick tickets and Deposco SOs) matching the same filters as query_worklist, " +
        "within the same view (optionally group_by a column), and of the asked customers' open pick tickets on no " +
        "line yet.",
      input_schema: {
        type: "object",
        properties: { ...queryProps(false), group_by: { type: "string" } },
      },
    });
  return tools;
}

/** The grid's state, in words: what the user is looking at. */
export function describeView(page: AskAiPage): string {
  const s = toSearch(page.mode, page.view);
  const parts = Object.entries(s)
    .filter(([k]) => k !== "d")
    .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join("|") : v}`);
  if (page.view.search) parts.push(`search box="${page.view.search}"`);
  return parts.length ? parts.join("; ") : "no filters";
}

export function systemPrompt(page: AskAiPage): SystemBlock[] {
  const tabs = Object.entries(page.counts)
    .map(([t, n]) => `${t} ${n}`)
    .join(", ");
  const thisWeek = weekRange(page.today, "this");
  const nextWeek = weekRange(page.today, "next");
  const text = [
    "You are Ask AI in Production & Routing, the AXNY warehouse's app that replaced the PRODUCTION CHECK OFF " +
      "workbook. Worklist: open lines — one per WMS # (A2000), or one per customer + PO (+ DC) (Deposco) — to " +
      "review (New, Updated, Split), route and produce. " +
      "History: finished lines, read-only.",
    APP_PAGES,
    `Today is ${page.today} (America/New_York). The user is on ${page.mode === "worklist" ? "the Worklist" : "History"}` +
      ` (${PAGES[page.mode].path}), tab "${page.view.tab}", with ${describeView(page)}. ` +
      `On screen: ${page.shown} lines. Lines per tab in this view: ${tabs}.` +
      (page.mode === "worklist" ? ` No WMS rows (open pick tickets without a WMS # yet): ${page.noWms}.` : "") +
      ` By source, with the other filters: A2000 ${page.sourceCounts.A2000}, Deposco ${page.sourceCounts.Deposco}.` +
      (page.mode === "worklist" ? ` Deposco orders waiting for a group (not lines yet): ${page.deposcoWaiting}.` : ""),
    "## Definitions",
    ...ROUTING_DEFINITIONS.map((d) => `- ${d}`),
    `- Worklist lines: ${WORKLIST_GUIDANCE}`,
    `- History lines: ${HISTORY_GUIDANCE}`,
    `- "This week" is ${thisWeek.from} to ${thisWeek.to} (today through Sunday); "next week" is ${nextWeek.from} ` +
      `to ${nextWeek.to} (Monday to Sunday). Use exactly these dates for them.`,
    "## Rules",
    "- Take every number from a tool answer, never from memory or a guess.",
    "- A count is within the user's current view by default: the tool answer's scope says which. Say that scope " +
      "in brackets after the number. When say_without_view is true, also give matched_without_view in a second " +
      "sentence, e.g. '79 lines are past deadline (PE + HT, received in the last 2 weeks). Across all warehouses " +
      "and dates: 244.' Leave a part of the view out (ignore_view) only when the question asks for it ('in all " +
      "warehouses', 'any date').",
    "- The Worklist's buckets are its tabs; use their names: Past deadline, Route now (Route by today or " +
      "tomorrow), Window open, Not open yet, Routed · in production, On hold, No routing rule. 'Route today', " +
      "'must be routed today', 'now or late' = Past deadline + Route now (filters.state_tab both, group_by " +
      "state_tab): the page's 'N to route now or late'. Say it in those words, e.g. '106 lines to route now or " +
      "late: 79 past deadline, 27 route now.' Never answer it with route_by = today.",
    "- 'Which customers need a routing rule': query_worklist with filters.state_tab No routing rule, group_by " +
      "customer; a manager sets one in Admin → Routing rules (the No routing rule card's link). rule_source says " +
      "where a line's window came from and its days ('From A2000 · code BEALLS in this group · 10 / 3'): asked which window " +
      "a customer's lines use, give the days and that code.",
    "- You only read. You cannot change, approve or delete a line, and must not offer to; the user edits in the " +
      "grid. apply_view only changes what the grid shows.",
    '- MIXED Deposco statuses: query_worklist with filters.deposco_mixed ["Yes"]; list deposco_sos to say which SO is ' +
      "behind (the team fixes it in Deposco). 'Waiting for a group': the deposco_waiting tool, never query_worklist.",
    "- Lines are never moved, merged or split in this app: A2000 splits and merges orders by WMS #. Never suggest " +
      "regrouping here; a Split or Updated line is how the app shows what A2000 did.",
    "- A value that matches nothing comes back with close values (no_such_value): use the closest one and say so.",
    "- similar_values_not_included lists other values like the one asked on lines left out (e.g. an outlet " +
      "account with its own shown name): say they were not counted, and include them when the question clearly " +
      "means them too.",
    "- A customer asked by its shown name covers every code showing it (Admin → Customers): " +
      "customer_codes_included lists them with their lines — name them, e.g. 'BEALLS (BEALLS, BEALLSOUTL, " +
      "C004957…)'. A code there 'kept separate' counts with its own routing window: say so, with its rule_source.",
    "- History matches a customer across its workbook spellings (case, spaces, punctuation): say the names " +
      "listed under customer_names_included, and pass all of them to apply_view.",
    page.myCustomers.labels.length
      ? `- "My customers" means this user's assigned customers (Admin → Customer assignments): ` +
        `${page.myCustomers.labels.join(", ")}; customer codes ${page.myCustomers.codes.slice(0, 60).join(", ")}. ` +
        (page.myCustomers.on
          ? "The grid shows only them now (the My customers chip), and counts are within them."
          : "The grid shows every customer now.")
      : '- This user has no assigned customers: for "my customers", say so (a manager assigns them in Admin).',
    page.manager
      ? "- This user is a manager: order $ values come from the order_value tool only."
      : "- This user cannot see order $ values. Never give, estimate or imply one; say values are for managers.",
    "- Answer briefly, with the lines' customers and dates where it helps. Name a customer by its shown name " +
      "(customer, as the grid shows it) with its code in brackets — and the original name too when it differs — " +
      "and a division by its abbreviation (division_abbrev) with the full name in brackets: ROSSSTORES (ROSS " +
      "STORES), MACY'S (MACYS, 15768), HANDBAG (HANDBAGS). A question by either a shown name or an original name " +
      "finds the line: filter customer with the name as asked.",
  ].join("\n");
  return [{ type: "text", text }];
}

/** A tool's request to `/api/routing/ask/<path>`: the page's own data API. */
export const askPath = (path: string) => `${ROUTING_API}/ask/${path}`;
